import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import IamBrokeredSessionMonitor from '@devnw/cyber-iam/iam/BrokeredSessionMonitor/BrokeredSessionMonitor'
import type { BrokeredSession as IamBrokeredSession } from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../../api/genClient'
import { useActiveTenant } from '../../layout/TenantSwitcher'
import { PamLoading, PamError, PamEmpty } from '../../components/PamStateComponents'
import { PamBreadcrumbs } from '../../components/PamBreadcrumbs'
import {
  toIamBrokeredSession,
  type WireBrokeredSession,
} from '../../api/brokeredSessionMapping'
import { CommandLogView } from './CommandLogView'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Action failed'
}

const POLL_INTERVAL_MS = 5000

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

/**
 * OIDCPAM Gate-6 Phase 5c: migrated off MUI onto
 * @devnw/cyber-iam/iam/BrokeredSessionMonitor — the exact parity component
 * built from this page. The component now owns the state-gated pause/
 * resume/kill actions (kill behind its own inline two-phase confirm, per-
 * action busy/error state); this page still owns the API calls, the 5s
 * polling cadence (POLL_INTERVAL_MS, unchanged — the library NEVER polls),
 * tenant/routing, and the wire-mapping (../../api/brokeredSessionMapping —
 * fixing a latent camelCase/snake_case bug, see that file's header). The
 * command log is composed via CommandLogView in the monitor's `children`
 * slot, exactly as it was nested under the pre-migration single-tab MUI
 * Tabs (which added no functionality beyond the one always-visible tab).
 */
export const BrokeredSessionDetailPage = (): JSX.Element => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [session, setSession] = useState<IamBrokeredSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const pollerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadMonitor = useCallback((): void => {
    if (!sessionId) return
    client
      .adminMonitorBrokeredSession({ tenantId: tenant, sessionId })
      .then((s) => {
        // Defensive: preserve the pre-migration "session not found" branch
        // for a falsy resolved value instead of throwing inside .then (which
        // would otherwise misroute a missing session into the fetch-error
        // path via toIamBrokeredSession's unconditional field access).
        setSession(
          s ? toIamBrokeredSession(s as unknown as WireBrokeredSession) : null
        )
        setError(null)
      })
      .catch((e: unknown) => {
        setError(errMessage(e))
      })
      .finally(() => setLoading(false))
  }, [client, tenant, sessionId])

  // Initial load
  useEffect(() => {
    setLoading(true)
    loadMonitor()
  }, [loadMonitor])

  // Polling every 5s — unchanged cadence; the library never polls (host
  // concern per the Gate-6 shared contract).
  useEffect(() => {
    if (pollerRef.current) clearInterval(pollerRef.current)
    pollerRef.current = setInterval(loadMonitor, POLL_INTERVAL_MS)
    return () => {
      if (pollerRef.current) clearInterval(pollerRef.current)
    }
  }, [loadMonitor])

  const handlePause = async (): Promise<void> => {
    if (!sessionId) return
    await client.adminPauseBrokeredSession({ tenantId: tenant, sessionId })
  }

  const handleResume = async (): Promise<void> => {
    if (!sessionId) return
    await client.adminResumeBrokeredSession({ tenantId: tenant, sessionId })
  }

  // IRREVERSIBLE — the component gates this behind its own inline two-phase
  // confirm before ever calling onKill. Preserves the pre-migration
  // reason:'admin_kill' payload exactly (the component's confirm collects no
  // free-text reason).
  const handleKill = async (): Promise<void> => {
    if (!sessionId) return
    await client.adminKillBrokeredSession({
      tenantId: tenant,
      sessionId,
      reason: 'admin_kill',
    })
  }

  // Reload after a successful pause/resume/kill (the component clears its
  // own busy/confirm state itself once its action promise resolves).
  const handleActionComplete = (): void => {
    loadMonitor()
  }

  if (loading && !session) return <PamLoading />
  if (error && !session) return <PamError message={error} />
  if (!session) return <PamEmpty message="Session not found" />

  const idLabel = sessionId ? sessionId.slice(0, 8) + '...' : '...'

  return (
    <PageContainer>
      <PamBreadcrumbs
        crumbs={[
          { label: 'Brokered Sessions', to: '/admin/brokered-sessions' },
          { label: idLabel },
        ]}
      />

      {error != null && error !== '' && (
        <PamError message={`Monitor poll error: ${error}`} />
      )}

      <IamBrokeredSessionMonitor
        testID="brokered-session-monitor"
        title="Session Detail"
        session={session}
        onPause={handlePause}
        onResume={handleResume}
        onKill={handleKill}
        onActionComplete={handleActionComplete}
        onRefresh={loadMonitor}
        refreshHint="Auto-refreshes every 5s"
      >
        <CommandLogView tenantId={tenant} sessionId={session.id} />
      </IamBrokeredSessionMonitor>
    </PageContainer>
  )
}
